import { AppForm } from '../../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidator, fromToDate } from '../../../../../../../common/forms/custom.validators';
import { ParcelAppEntityParcelWrite, RoadOccupationStageAppEntityApplicationRead } from '../../../../../../../openapi';
import { calcDaysBetweenDates, toNumber } from '../../../../../../../services/helpers';

export function toAdPlacementStageFormValue(v: RoadOccupationStageAppEntityApplicationRead): AdPlacementStageFormValue {
  return {
    startDate: v?.startDate || null,
    endDate: v?.endDate || null,

    geometry: v?.geometry || '',

    provideRoadNumberAndName: !!v?.provideRoadNumberAndName,
    city: v?.city || '',

    roadNumber: v?.roadNumber || '',
    roadName: v?.roadName || '',
    street: v?.street || '',

    parcels: v?.parcels || [],

    dayQuantity: v?.dayQuantity || null,
    adQuantity: v?.adQuantity + '',
    adType: v?.adType || '',
    adWidth: (v?.adWidth || 0).toFixed(2).replace('.', ','),
    adLength: (v?.adLength || 0).toFixed(2).replace('.', ','),
    adArea: (v?.adArea || 0).toFixed(2).replace('.', ','),

    adContent: v?.adContent || ''
  };
}

export interface AdPlacementStageFormValue {
  startDate: Date | null;
  endDate: Date | null;
  dayQuantity: number | null;

  geometry: string | null;

  provideRoadNumberAndName: boolean;
  city: string;

  roadName: string | null;
  roadNumber: string | null;
  street: string | null;

  parcels: ParcelAppEntityParcelWrite[];

  adType: string;

  adLength: string;
  adWidth: string;
  adArea: string;
  adQuantity: string | null;

  adContent: string;
}

export class AdPlacementStageForm extends AppForm {
  constructor() {
    super({
      startDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat(), CustomValidator.minDate(new Date())] }),
      endDate: new FormControl(null, { validators: [Validators.required, CustomValidator.isValidDateFormat()] }),
      dayQuantity: new FormControl(null),
      geometry: new FormControl(null),

      provideRoadNumberAndName: new FormControl(true),
      city: new FormControl(
        '',
        { nonNullable: true, validators: [Validators.maxLength(255), Validators.required] }
      ),

      parcels: new FormControl([], {
        nonNullable: true,
        validators: [
          Validators.minLength(1),
          Validators.required
        ]
      }),

      adType: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      adLength: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adWidth: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adArea: new FormControl(null, [Validators.required, Validators.maxLength(255)]),
      adQuantity: new FormControl(null, [Validators.required, Validators.maxLength(255)]),

      adContent: new FormControl(null, [Validators.required, Validators.maxLength(5000)])
    }, {
      validators: [
        fromToDate('startDate', 'endDate')
      ]
    });
    this.provideRoadNumberAndNameChange(true);
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();

    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));
      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public get startDate(): FormControl {
    return this.get('startDate') as FormControl;
  }

  public get endDate(): FormControl {
    return this.get('endDate') as FormControl;
  }

  public get dayQuantity(): FormControl {
    return this.get('dayQuantity') as FormControl;
  }

  public startDateChange(v: Date): void {
    if (v && this.endDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(v), new Date(this.endDate.value))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public endDateChange(v: Date): void {
    if (v && this.startDate.value) {
      this.dayQuantity.setValue(
        calcDaysBetweenDates(new Date(this.startDate.value), new Date(v))
      );
    } else {
      this.dayQuantity.setValue('');
    }
  }

  public get adType(): FormControl {
    return this.get('adType') as FormControl;
  }

  public get adWidth(): FormControl {
    return this.get('adWidth') as FormControl;
  }

  public get adLength(): FormControl {
    return this.get('adLength') as FormControl;
  }

  public get adQuantity(): FormControl {
    return this.get('adQuantity') as FormControl;
  }

  public get adArea(): FormControl {
    return this.get('adArea') as FormControl;
  }

  public calcAdArea(): void {
    if (!this.adType.value) {
      this.adArea.setValue('');
      return;
    }
    const adWidth: number = toNumber(this.adWidth.value);
    const adLength: number = toNumber(this.adLength.value);
    const adQuantity: number = toNumber(this.adQuantity.value);
    this.adArea.setValue(`${adWidth * adLength * adQuantity}`);
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get geometry(): FormControl {
    return this.get('geometry') as FormControl;
  }

  public get values(): AdPlacementStageFormValue {
    return {
      ...this.value,
      roadName: this.roadName?.value || null
    };
  }

  public override patchFormValues(value: AdPlacementStageFormValue) {
    this.provideRoadNumberAndNameChange(!!value?.provideRoadNumberAndName);
    super.patchFormValues({
      ...value,
      provideRoadNumberAndName: value.provideRoadNumberAndName
    });
  }

}
